export type AssessmentMethodology = "KEYSTONE_2.0" | "KEYSTONE_3.0";

export type InterventionType =
  | "Afforestation, Reforestation, and Revegetation"
  | "Improved Agricultural Land Management"
  | "Improved Land Management"
  | "Avoided Deforestation"
  | "Restoring and Protecting Wetland Ecosystems"
  | "Biochar"
  | "Improved Forest Management"
  | "Habitat Restoration and Creation"
  | "Tree Planting";

export type CarbonCreditProgramme =
  | "Verified Carbon Standard (VCS)"
  | "Climate Action Reserve (CAR)"
  | "Gold Standard"
  | "Plan Vivo"
  | "Equitable Earth (EE)"
  | "International Carbon Registry (ICR)"
  | "Woodland Carbon Code (WCC)"
  | "Peatland Carbon Code (PCC)"
  | "Puro Standard"
  | "DEFRA & Natural England"
  | "Other";

export const SELECTABLE_NOT_READY_FOR_SALE_REASONS = [
  "Dropped",
  "Out of Credits",
  "Assessment in Progress",
  "Waiting for Contract",
  "Waiting for Media",
  "Paused",
] as const;

export const NOT_RECORDED_REASON = "Not Recorded";

export type SelectableNotReadyForSaleReason =
  (typeof SELECTABLE_NOT_READY_FOR_SALE_REASONS)[number];

export const NOT_READY_FOR_SALE_REASONS = [
  ...SELECTABLE_NOT_READY_FOR_SALE_REASONS,
  NOT_RECORDED_REASON,
] as const;

export type NotReadyForSaleReason = (typeof NOT_READY_FOR_SALE_REASONS)[number];

export type AssetType =
  | "Tree Planting"
  | "Biodiversity Credits"
  | "Innovation"
  | "Carbon Credits: Removal"
  | "Carbon Credits: Avoidance"
  | "Carbon Credits: Avoidance & Removal";

export type MappedDropDownOptions<Value extends string> = {
  value: Value;
  label: string;
};

export type ApiOutcome = {
  unit: string;
  value: number;
  isUnitOfSale: boolean;
  startDate?: number;
  endDate?: number;
};

export type OutcomesByVersion = {
  v2: Array<ApiOutcome>;
  v3: Array<ApiOutcome>;
};

export type Date = {
  start: number;
  end: number;
};

export type Coordinates = {
  latitude: number;
  longitude: number;
};

export type DateRange = {
  start: number | null;
  end: number | null;
};

export type ProgrammeInfo = {
  carbonCreditProgramme: Array<string> | string;
  projectDescriptionDocument: string | null;
  accountingMethodology: string | null;
  assetType: AssetType | null;
};

export type InternalUseOnly = {
  websiteUrl: string | null;
  xeroAccountNumber: string | null;
  researchTeamNotes: string | null;
  additionalResourceLink: string | null;
};

export type RiskMonitoring = {
  isEnabled: boolean;
  buffer?: number | null;
  geospatialFile?: File | null;
  geospatialDataId?: string;
};

export type Outcomes = Array<{
  value: number | string;
  unit: string;
  isUnitOfSale: boolean;
  startDate?: number | null;
  endDate?: number | null;
}>;

export type Intervention = {
  action: Array<string | undefined> | null;
  ecosystem: Array<string | undefined> | null;
};

export type Scores = {
  total?: number;
};
export type Benefits = {
  quantity?: string;
  description?: string;
};
export type Pillar = {
  scores?: Scores;
  benefits?: Benefits;
};
export type Pillars = {
  carbon?: Pillar;
  biodiversity?: Pillar;
  people?: Pillar;
};

export type ScoresPayload = {
  total?: number | null;
};
export type BenefitsPayload = {
  quantity?: string | null;
  description?: string | null;
};
export type PillarPayload = {
  scores?: ScoresPayload;
  benefits?: BenefitsPayload;
};
export type PillarsPayload = {
  carbon?: PillarPayload;
  biodiversity?: PillarPayload;
  people?: PillarPayload;
};
export type CommercialInfo = {
  minimumOrder?: number;
};

export type Narrative = {
  projectInformation?: string | null;
  areaThroughTime?: string | null;
  goodForPeople?: string | null;
  goodForEarth?: string | null;
};

export type SolutionInListApiResponse = {
  id: string;
  name: string;
  developer?: string;
  shortDescription?: string;
  unit?: string;
  unitOfSale?: string;
  commercialInformation?: CommercialInfo;
  interventionType?: InterventionType | null;
  SDG?: Array<number>;
  location?: { type: "Point"; coordinates: Array<number> };
  assessmentMethodology?: AssessmentMethodology | null;
  assetType?: AssetType | null;
  coBenefitEducation?: number | string | null;
  coBenefitEmployment?: number | string | null;
  country: Array<string>;
  isReadyForSale: boolean;
  notReadyForSaleReason?: NotReadyForSaleReason | null;
  isUsedForCarbonOffsetting: boolean;
  accountingMethodology?: string | null;
  projectDescriptionDocument?: string | null;
  websiteUrl?: string;
  images?: {
    default?: string;
  };
};

export type CommercialInformation = {
  minimumOrder?: number | null;
};

export type SatelliteImage = {
  mediaLink: string;
  caption: string;
};

export type DetailedSolutionApiResponse = {
  id: string;
  name: string;
  vbcWebsiteDisplayName?: string;
  developer?: string;
  shortDescription?: string;
  country: Array<string>;
  isReadyForSale: boolean;
  notReadyForSaleReason?: NotReadyForSaleReason | null;
  commercialInformation?: CommercialInfo;
  isUsedForCarbonOffsetting?: boolean;
  interventionType?: InterventionType | null;
  assessmentMethodology?: AssessmentMethodology | null;
  SDG: Array<number>;
  developmentStage?: Array<string>;
  date?: Date;
  coordinates?: Coordinates;
  interventions?: Array<Intervention>;
  outcomes?: OutcomesByVersion;

  pillars?: Pillars;

  programmeInfo?: {
    carbonCreditProgramme?: Array<CarbonCreditProgramme>;
    projectDescriptionDocument?: string;
    accountingMethodology?: string;
    assetType?: AssetType | null;
  };
  internalUseOnly?: {
    websiteUrl?: string;
    xeroAccountNumber?: string;
    researchTeamNotes?: string;
    additionalResourceLink?: string;
    stripeProductUrl?: string;
  };
  narrative?: Narrative;
  satelliteImagery?: Array<SatelliteImage>;
  locationPinMapLink?: string;
  riskMonitoring?: RiskMonitoring;
};

export type SolutionPayload = {
  name: string;
  vbcWebsiteDisplayName?: string | null;
  developer: string | null;
  shortDescription: string | null;
  country: Array<string>;
  coordinates: Coordinates | null;
  developmentStage: Array<string>;
  assessmentMethodology: AssessmentMethodology | null;
  interventionType?: InterventionType | null;
  SDG: Array<number>;
  date: DateRange;
  isReadyForSale: boolean;
  notReadyForSaleReason?: NotReadyForSaleReason | null;
  isUsedForCarbonOffsetting: boolean;
  interventions: Array<Intervention>;
  outcomes: Outcomes;
  pillars?: PillarsPayload;
  programmeInfo: ProgrammeInfo;
  internalUseOnly: InternalUseOnly;
  commercialInformation: CommercialInformation;
  narrative?: Narrative;
  satelliteImagery?: Array<SatelliteImage>;
  locationPinMapLink?: string | null;
  riskMonitoring?: RiskMonitoring;
};
